import React, { useRef, useState } from "react";
import { Button, Tooltip } from "@mui/material";
import { LabelFilter } from "./TranslationStats";
import Icon from "../../ui/Icon";
import { getButtonStyles } from "../../../utils/ApiUtils/TranslationHelper";
import TranslationProgressBar from "../progress/TranslationLabelsProgressBar";

interface TranslationStatsHeaderProps {
  totalItems: number;
  pendingItems: number;
  translatedItems: number;
  aiTranslatedItems?: number;
  initialLabelsToTranslate: number;
  needsReviewItems?: number;
  activeFilter: LabelFilter;
  onFilterChange: (filter: LabelFilter) => void;
  onSendForReview?: () => void;
  onClose?: () => void;
  onExport?: () => void;
  onImport?: (file: File) => void;
  selectedCount?: number;
  onValidateSelected?: () => void;
  onToggleExpand: () => void;
  isExpanded: boolean;
  review: boolean;
  onToggleShortCuts: () => void;
}

const TranslationStatsHeader: React.FC<TranslationStatsHeaderProps> = ({
  totalItems,
  pendingItems,
  translatedItems,
  aiTranslatedItems,
  initialLabelsToTranslate,
  needsReviewItems,
  activeFilter,
  onFilterChange,
  onSendForReview,
  onClose,
  onExport,
  onImport,
  selectedCount = 0,
  onValidateSelected,
  onToggleExpand,
  isExpanded,
  review,
  onToggleShortCuts,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFilterClick = (filter: LabelFilter) => {
    onFilterChange(filter);
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onImport?.(file);
    }
    // Reset so selecting the same file again still fires onChange.
    e.target.value = "";
  };

  return (
    <div
      className="stats-header-container flex items-center justify-between gap-4 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors duration-200 min-h-[80px] relative z-10"
      onClick={onToggleExpand}
    >
      <div className="flex items-center gap-3">
        <h3 className="text-lg font-semibold text-gray-800 whitespace-nowrap">Translation Progress</h3>
        <div className={`transform transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}>
          <Icon icon="down" />
        </div>
      </div>

      <div className="flex gap-2">
        <button
          className={getButtonStyles("total", activeFilter, "teal")}
          onClick={(e) => {
            e.stopPropagation();
            handleFilterClick("total");
          }}
        >
          {totalItems} Total
        </button>
        <button
          className={getButtonStyles("pending", activeFilter, "yellow")}
          onClick={(e) => {
            e.stopPropagation();
            handleFilterClick("pending");
          }}
        >
          <Icon icon="modify" />
          {pendingItems} Pending
        </button>
        <button
          className={getButtonStyles("translated", activeFilter, "purple")}
          onClick={(e) => {
            e.stopPropagation();
            handleFilterClick("translated");
          }}
        >
          <Icon icon="translate" />
          {translatedItems} Translated
        </button>
        <button
          className={getButtonStyles("AI Translated", activeFilter, "green")}
          onClick={(e) => {
            e.stopPropagation();
            handleFilterClick("AI Translated");
          }}
        >
          <Icon icon="magic" />
          {aiTranslatedItems ?? 0} AI Translated
        </button>
      </div>

      <TranslationProgressBar initial={initialLabelsToTranslate} pending={pendingItems} />

      <div className="flex items-center gap-2">
        {/* Small Rounded Info Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleShortCuts();
          }}
          className="w-8 h-8 rounded-full text-primary-dark shadow-md hover:shadow-lg flex items-center justify-center text-xs font-semibold border border-primary-light"
          title="View keyboard shortcuts"
        >
          <Icon icon="info" />
        </button>
        <Tooltip title={selectedCount > 0 ? `Validate ${selectedCount} selected label(s)` : "Select labels to validate"}>
          <span onClick={(e) => e.stopPropagation()}>
            <Button
              variant="contained"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onValidateSelected?.();
              }}
              color="success"
              className="min-w-fit"
              disabled={selectedCount === 0}
            >
              <Icon icon="check" />
              <span className="action-btn-label ml-1">Validate</span>
              {selectedCount > 0 ? <span className="ml-1">({selectedCount})</span> : ""}
            </Button>
          </span>
        </Tooltip>
        <input
          ref={fileInputRef}
          type="file"
          accept=".tsv"
          className="hidden"
          onClick={(e) => e.stopPropagation()}
          onChange={handleFileSelected}
        />
        <Tooltip title={"Export translation file"}>
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onExport?.();
            }}
            color="primary"
            className="min-w-fit"
          >
            <Icon icon="upload" />
            <span className="action-btn-label ml-1">Export</span>
          </Button>
        </Tooltip>
        <Tooltip title={"Import translation file"}>
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleImportClick();
            }}
            color="primary"
            className="min-w-fit"
          >
            <Icon icon="download" />
            <span className="action-btn-label ml-1">Import</span>
          </Button>
        </Tooltip>
        <Tooltip title={"Close"}>
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onClose?.();
            }}
            color="error"
            className="min-w-fit"
          >
            <Icon icon="close" />
            <span className="action-btn-label ml-1">Close</span>
          </Button>
        </Tooltip>
        <Tooltip title={!review ? "send to review" : " cannot send to review"}>
          <span onClick={(e) => e.stopPropagation()}>
            <Button
              variant="contained"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onSendForReview?.();
              }}
              color="primary"
              className="min-w-fit flex"
              disabled={review}
            >
              <Icon icon="step" />
              <span className="action-btn-label ml-1">REVIEW</span>
            </Button>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

export default TranslationStatsHeader;
